import { createSHA256 } from 'hash-wasm';
import { api } from './apiClient';
import { base64ToBytes, encryptPart, importChunkKey } from './chunkCrypto';

/** Mirrors the API's DIRECT_UPLOAD_MAX_BYTES default; the server enforces the real limit. */
export const MAX_UPLOAD_BYTES = 5 * 1024 ** 3;
const CONCURRENCY = 4;
const ATTEMPTS = 3;
const SIGN_BATCH = 20;
const GCM_TAG_BYTES = 16;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** One presigned PUT straight to S3 — plain XHR (S3 rejects our Bearer header) for upload progress. */
function putPart(url, body, onLoaded, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = (event) => onLoaded(event.loaded);
    xhr.onload = () => {
      const etag = xhr.getResponseHeader('ETag');
      if (xhr.status >= 200 && xhr.status < 300 && etag) return resolve(etag);
      const message = etag
        ? `Upload failed (${xhr.status})`
        : 'Storage did not return an ETag (check the bucket CORS ExposeHeaders)';
      reject(Object.assign(new Error(message), { status: xhr.status }));
    };
    xhr.onerror = () => reject(Object.assign(new Error('Network error while uploading'), { status: 0 }));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

/**
 * Upload a file browser → S3 in parts, 4 at a time, with per-part retries.
 *
 * `begin()` calls the feature's own begin endpoint and resolves to
 * `{ uploadToken, partSize, partCount, parts, encryption? }`. When the session
 * carries `encryption`, every part is encrypted on this device before it is
 * sent. Resolves to the complete endpoint's response.
 */
export async function uploadDirect({ file, begin, onProgress, signal, skipAuth = false }) {
  if (!file.size) throw new Error('Empty file');
  if (file.size > MAX_UPLOAD_BYTES) throw new Error('File exceeds the 5 GB upload limit');

  const session = await begin();
  const { uploadToken, partSize, partCount } = session;
  const post = (path, body) => api.post(path, body, { skipAuth });

  const key = session.encryption ? await importChunkKey(session.encryption.key) : null;
  const ivPrefix = session.encryption ? base64ToBytes(session.encryption.ivPrefix) : null;
  const hasher = key ? null : await createSHA256();
  hasher?.init();

  // Abort in-flight parts on the first hard failure, or when the caller cancels.
  const controller = new AbortController();
  signal?.addEventListener('abort', () => controller.abort(), { once: true });

  const total = key ? file.size + GCM_TAG_BYTES * partCount : file.size;
  const loaded = new Map();
  let lastPercent = -1;
  const report = () => {
    let sum = 0;
    for (const bytes of loaded.values()) sum += bytes;
    // Hold 100 until the server has registered the file.
    const percent = Math.min(99, Math.floor((sum / total) * 100));
    if (percent > lastPercent) onProgress?.((lastPercent = percent));
  };

  const urls = new Map(session.parts.map((p) => [p.partNumber, p.url]));
  const signing = new Map();
  const sign = async (partNumbers) => {
    const { data } = await post('/uploads/parts', { uploadToken, partNumbers });
    for (const p of data.parts) urls.set(p.partNumber, p.url);
  };
  const urlFor = async (n) => {
    if (!urls.has(n)) {
      // One request per batch of 20, shared by whichever workers need it.
      const first = n - ((n - 1) % SIGN_BATCH);
      if (!signing.has(first)) {
        const batch = [];
        for (let i = first; i < first + SIGN_BATCH && i <= partCount; i++) {
          if (!urls.has(i)) batch.push(i);
        }
        signing.set(first, sign(batch).finally(() => signing.delete(first)));
      }
      await signing.get(first);
      if (!urls.has(n)) await sign([n]);
    }
    return urls.get(n);
  };

  // Reads are chained so the hash sees the bytes in file order.
  let reading = Promise.resolve();
  const readPart = (n) => {
    const read = reading.then(async () => {
      const blob = file.slice((n - 1) * partSize, n * partSize);
      const bytes = new Uint8Array(await blob.arrayBuffer());
      hasher?.update(bytes);
      return bytes;
    });
    reading = read.catch(() => undefined);
    return read;
  };

  const sendPart = async (n, body) => {
    for (let attempt = 1; ; attempt++) {
      try {
        const url = await urlFor(n);
        const etag = await putPart(
          url,
          body,
          (bytes) => {
            loaded.set(n, bytes);
            report();
          },
          controller.signal,
        );
        loaded.set(n, body.byteLength);
        report();
        return etag;
      } catch (error) {
        loaded.set(n, 0);
        if (controller.signal.aborted || attempt >= ATTEMPTS) throw error;
        if (error.status === 403) urls.delete(n); // presigned URL expired → re-sign
        await sleep(1000 * 2 ** (attempt - 1));
      }
    }
  };

  const parts = [];
  let next = 1;
  const worker = async () => {
    while (next <= partCount && !controller.signal.aborted) {
      const n = next++;
      const plain = await readPart(n);
      const body = key ? await encryptPart(key, ivPrefix, n, plain) : plain;
      parts.push({ partNumber: n, etag: await sendPart(n, body) });
    }
  };

  try {
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, partCount) }, worker));
    if (controller.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
  } catch (error) {
    controller.abort();
    post('/uploads/abort', { uploadToken }).catch(() => {});
    throw error;
  }

  const { data } = await post('/uploads/complete', {
    uploadToken,
    parts,
    sha256: hasher ? hasher.digest('hex') : undefined,
  });
  onProgress?.(100);
  return data;
}
